import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

/* ── SmarterHoops Modal Standard ──────────────────────────────────
   • Drops down from the top of the viewport (anchored to the top
     nav bar) so iOS keyboards can never cover the input fields.
   • Sharp top edges (flush with the safe-area / status bar) and
     rounded-bottom corners (20px).
   • Dark navy header (#0B1824) matching the top nav, white title
     text, X close button on the right.
   • Body of the modal sits below the navy header on a white surface.
   • Backed by a dimmed, blurred overlay that lets the underlying
     content remain visible at low contrast.
   ───────────────────────────────────────────────────────────────── */

// Pass the `hideClose` flag from DialogContent to DialogHeader so a single
// consumer-side prop on DialogContent still works.
const DialogContext = React.createContext({ hideClose: false })

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    style={{ background: 'rgba(11,24,36,0.6)', backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)' }}
    className={cn(
      "fixed inset-0 z-[299] duration-100 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef(({ className, children, hideClose = false, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogContext.Provider value={{ hideClose }}>
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          // Anchored to the top of the viewport, full-width on mobile,
          // capped at sm:max-w-lg on desktop and centered horizontally.
          "fixed top-0 left-0 right-0 z-[300] mx-auto w-full sm:max-w-lg",
          // White surface, sharp top edges, rounded bottom corners only.
          "bg-white rounded-b-[20px] shadow-[0_8px_32px_rgba(0,0,0,0.35)]",
          // Padding applies to the body. DialogHeader uses negative
          // margins to extend the navy bar edge-to-edge.
          "px-5 pb-5",
          // Cap height so the user can still see context behind the
          // modal and so the iOS keyboard has room below the inputs.
          "max-h-[85vh] overflow-y-auto overscroll-contain",
          // Slide in from top, slide out to top — matches the "drops
          // down from the top nav" mental model. Snappy 120ms duration
          // so the modal feels instant during in-game stat capture.
          "duration-[120ms] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
          className
        )}
        {...props}>
        {children}
      </DialogPrimitive.Content>
    </DialogContext.Provider>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({ className, children, ...props }) => {
  const { hideClose } = React.useContext(DialogContext)
  return (
    <div
      data-dialog-header=""
      className={cn(
        // -mx-5 cancels DialogContent's horizontal padding so the navy
        // bar spans edge-to-edge. mb-5 provides breathing room before
        // the body content.
        "relative -mx-5 -mt-0 mb-5 px-5 flex flex-col gap-1 text-left text-white",
        // The mb-5 must be cancelled when DialogHeader is the *only*
        // child (e.g., empty-state confirmation). Consumers can pass
        // `mb-0` via className to override.
        className
      )}
      style={{
        background: '#0B1824',
        // Push down by the iOS safe-area so the navy bar sits below
        // the status bar / notch but visually still anchors to the
        // very top of the screen.
        paddingTop: 'calc(env(safe-area-inset-top) + 1rem)',
        paddingBottom: '1rem',
        paddingRight: hideClose ? undefined : '3rem',
      }}
      {...props}>
      {children}
      {!hideClose && (
        <DialogPrimitive.Close
          className="absolute right-3 rounded-full p-1.5 opacity-90 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-white/40 disabled:pointer-events-none transition-opacity"
          style={{
            top: 'calc(env(safe-area-inset-top) + 0.75rem)',
          }}
          data-testid="dialog-close"
        >
          <X className="h-5 w-5 text-white" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      )}
    </div>
  )
}
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({ className, ...props }) => (
  <div
    className={cn("flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:space-x-2 pt-2 pb-1", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-bold leading-tight tracking-tight text-white uppercase",
      className
    )}
    style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-slate-300", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
